import * as React from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import './notifier.module.scss';
import { CurrentUserState } from '../../../../mainv2/states/CurrentUserState';
import { NotificationBar, NotificationSubTitle, NotificationTitle } from './NotificationBar';
import { routingStore } from '../../../../common/stores/routing';
import { V3Routes } from '../../../routes';
import { OverlayContent } from '../../../../commonv3/components/molecules/layout/OverlayPage';
import { NonSpBr, SpBr } from '../../../../commonv3/components/atoms/layouts/br';
import { WithdrawalNotification } from './WithdrawalNotification';
import { UpdateSubscriptionNotification } from './UpdateSubscriptionNotification';
import moment from 'moment-timezone';
import { Button } from '@sheinc/shelikes-design-system';

const FailedTransactionNotification = () => (
  <NotificationBar
    id="failed-payment-notification"
    type="danger"
    buttonLabel="支払い一覧で確認する"
    onClick={() => routingStore.push(V3Routes.payments())}
  >
    <NotificationTitle>
      一部のお支払いが完了していないようです。
      <SpBr />
      支払い一覧から確認をお願いします。
    </NotificationTitle>
    <NotificationSubTitle>
      お支払いが確認できない状況が続くと、 サービスが利用できなくなる場合がございます。
    </NotificationSubTitle>
  </NotificationBar>
);

const HomeLocationNotification = () => (
  <NotificationBar
    type="warning"
    buttonLabel="設定を確認する"
    onClick={() => routingStore.push(V3Routes.homeLocation())}
  >
    <NotificationTitle>
      ホーム拠点の設定が完了していません。
      <SpBr />
      設定をご確認ください。
    </NotificationTitle>
    <NotificationSubTitle>最も良くご利用になる拠点の設定をお願いいたします。</NotificationSubTitle>
  </NotificationBar>
);

const FirstPaymentAfterMigrationNotification = ({ paymentStartsAt }: { paymentStartsAt: string | null }) => {
  const remainingDays = moment(paymentStartsAt).diff(moment().startOf('day'), 'day');
  if (remainingDays <= 0) {
    return null;
  }
  return (
    <NotificationBar type="warning" buttonLabel="設定を確認する" onClick={() => routingStore.push(V3Routes.plan())}>
      <NotificationTitle>旧チケット会員移行後の無料期間が、あと{remainingDays}日で終了します。</NotificationTitle>
      <NotificationSubTitle>
        その後は毎月受け放題プランにて自動で課金・継続されます。
        <NonSpBr />
        継続をご希望されない場合は、マイページからプラン変更or退会のお手続きをお願いいたします。
      </NotificationSubTitle>
    </NotificationBar>
  );
};

type Props = {
  userState: CurrentUserState;
};

export const Notifier = observer(
  class Notifier extends React.Component<Props> {
    render() {
      const { userState } = this.props;
      const closed = routingStore.location.pathname.match('/home/mypage/?|/home_location/update');
      if (closed) {
        return null;
      }
      if (userState.isDeactivated) {
        return (
          <OverlayContent
            open={!closed}
            onRequestClose={() => undefined}
            childElem={(st, setRef) => {
              return (
                <div styleName="modal" id="failed-payment-service-lock-modal">
                  <div styleName={classNames('modal-body', st)} ref={setRef}>
                    <div styleName="modal-title">支払いが1ヶ月間行われなかったためサービスの利用を停止しております</div>
                    <div styleName="modal-inner">
                      下記ボタンから再支払いの設定を
                      <br />
                      していただくと利用できるようになります
                    </div>
                    <Button variant="danger" onClick={() => routingStore.push(V3Routes.payments())}>
                      再支払いの設定をする
                    </Button>
                  </div>
                </div>
              );
            }}
          />
        );
      }
      if (userState.hasFailedTransactions) {
        return <FailedTransactionNotification />;
      }

      if (
        !userState.homeLocation.loading &&
        !userState.homeLocation.isHomeLocationSet &&
        userState.preference.confirmedHomeLocationAlert
      ) {
        return <HomeLocationNotification />;
      }

      if (userState.isFirstPaymentAfterMigrationWithin(30, 'days')) {
        return <FirstPaymentAfterMigrationNotification paymentStartsAt={userState.firstPaymentDateAfterMigration} />;
      } else {
        return (
          <>
            <WithdrawalNotification userState={userState} />
            <UpdateSubscriptionNotification />
          </>
        );
      }
      return <WithdrawalNotification userState={userState} />;
    }
  }
);
